import {FirebaseAppCheck} from '@capacitor-firebase/app-check';
import {Capacitor} from '@capacitor/core';
import {FirebaseOptions, getApp, getApps, initializeApp} from 'firebase/app';
import {
  ReCaptchaEnterpriseProvider,
  getToken,
  initializeAppCheck,
  onTokenChanged,
} from 'firebase/app-check';
import {addGlobalHeaderToApiClient} from '@common/http/query-client';
import {getBootstrapData} from '@ui/bootstrap-data/bootstrap-data-store';

const HEADER = 'X-Firebase-AppCheck';
let nativeInitialization: Promise<void> | undefined;
let webInitialization: Promise<void> | undefined;

type EnforcementMode = 'off' | 'monitor' | 'enforce';

export function initializeNativeFirebaseAppCheck() {
  if (!Capacitor.isNativePlatform()) return Promise.resolve();
  nativeInitialization ??= initializeNativeWithoutBlockingMonitorMode();
  return nativeInitialization;
}

function initializeNativeWithoutBlockingMonitorMode() {
  // App Check's first token exchange can wait on provider networking or an
  // unregistered simulator debug token. Monitor mode is intentionally
  // observational, so it must not hold the native shell or reviewer login on
  // the launch spinner. Enforce mode still fails closed before API traffic.
  if (nativeMode() === 'monitor') {
    window.setTimeout(() => void initializeNative(), 0);
    return Promise.resolve();
  }
  return initializeNative();
}

async function initializeNative() {
  const mode = nativeMode();
  if (mode === 'off') return;

  try {
    await FirebaseAppCheck.initialize({
      isTokenAutoRefreshEnabled: true,
    });
    const result = await FirebaseAppCheck.getToken();
    setToken(result.token);
    await FirebaseAppCheck.addListener('tokenChanged', event => {
      setToken(event.token);
    });
  } catch (error) {
    setToken('');
    if (mode === 'enforce') throw error;
    console.warn('Firebase App Check is unavailable in monitor mode.');
  }
}

export function initializeWebFirebaseAppCheck() {
  if (Capacitor.isNativePlatform()) return Promise.resolve();
  webInitialization ??= initializeWeb();
  return webInitialization;
}

async function initializeWeb() {
  const config = getBootstrapData().firebase_app_check;
  const mode = normalizeMode(config?.mode);
  if (mode === 'off') return;

  try {
    if (!config?.site_key || !hasFirebaseOptions(config.firebase)) {
      throw new Error('Firebase App Check web configuration is incomplete.');
    }
    const app = getApps().length
      ? getApp()
      : initializeApp(config.firebase as FirebaseOptions);
    const appCheck = initializeAppCheck(app, {
      provider: new ReCaptchaEnterpriseProvider(config.site_key),
      isTokenAutoRefreshEnabled: true,
    });
    onTokenChanged(
      appCheck,
      result => setToken(result.token),
      () => setToken(''),
    );
    setToken((await getToken(appCheck)).token);
  } catch (error) {
    setToken('');
    if (mode === 'enforce') throw error;
    console.warn('Firebase App Check is unavailable in monitor mode.');
  }
}

function hasFirebaseOptions(
  options: Record<string, string | undefined> | undefined,
) {
  return !!(
    options?.apiKey &&
    options.authDomain &&
    options.appId &&
    options.projectId
  );
}

function nativeMode(): EnforcementMode {
  return normalizeMode(
    import.meta.env.VITE_FIREBASE_APP_CHECK_MODE as string | undefined,
  );
}

function normalizeMode(value: string | undefined): EnforcementMode {
  return value === 'monitor' || value === 'enforce' ? value : 'off';
}

function setToken(token: string) {
  addGlobalHeaderToApiClient(HEADER, token);
}
